import { useEffect } from "react";
import { useParams } from "react-router-dom";

import { Box, Container, Table, TableBody, TableContainer } from "@mui/material";
import ApprovalOutlinedIcon from "@mui/icons-material/ApprovalOutlined";

import ErrorChip from "../utils/ErrorChip";
import SectionHeader from "../utils/SectionHeader";
import ProtectedRoute from "../auth/ProtectedRoute";
import LinearIndeterminate from "../utils/LinearLoader";
import { useProxyApprove, useProxyReject } from "../../hooks/proxy/requests";
import { RequestSchema, useProxyRequestDetail } from "../../hooks/proxy/requests";
import { ClosedTableHeader, ClosedRow, PendingTableHeader, PendingRow } from "./Rows";

export const RequestWindow = ({ request }: { request: RequestSchema }) => {
  const { id, status } = request;
  const reject = useProxyReject();
  const approve = useProxyApprove();

  return (
    <Container maxWidth="xl" sx={{ boxShadow: 4, mb: 2 }}>
      <TableContainer>
        <Table aria-label="collapsible table">
          {status === "pending" ? <PendingTableHeader /> : <ClosedTableHeader />}

          <TableBody>
            {status === "pending" ? (
              <PendingRow key={id} reject={reject} approve={approve} request={request} defaultOpen={true} />
            ) : (
              <ClosedRow key={id} request={request} defaultOpen={true} />
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

const RequestDetail = () => {
  const { id = "" } = useParams();

  const request = useProxyRequestDetail({ id });
  const data = request.data?.data;

  const title = data?.subject ?? "--";

  useEffect(() => {
    document.title = "[semiauth] | requests";
  }, []); // run once on mount

  return (
    <ProtectedRoute>
      {request.isLoading ? (
        <LinearIndeterminate />
      ) : request.isError ? (
        <Box
          sx={{
            height: "88vh", // full height to center vertically (within the viewport)
            display: "flex", // use flexbox layout
            alignItems: "center", // center vertically
            justifyContent: "center" // center horizontally
          }}
        >
          <ErrorChip label={request.error.response?.data?.message} />
        </Box>
      ) : (
        <Container maxWidth="xl">
          <SectionHeader title={title} icon={<ApprovalOutlinedIcon />} />

          {!!data && <RequestWindow request={data} />}
        </Container>
      )}
    </ProtectedRoute>
  );
};

export default RequestDetail;
